import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Button,
  Dimmer,
  Dropdown,
  Grid,
  Input,
  Label,
  Loader,
  Message,
  Segment,
} from 'semantic-ui-react';

import { fetchM } from '../../actions/fetchM';
import { fetchTags } from '../../actions/tags';
import PICTURE_TYPES from '../../constants/pictureTypes';
import EditImage from '../../components/EditImage';
import { titleCase, symbolToSpace } from '../../utils/strings';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';

class UploadPictureForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    onChange: PropTypes.func,
    tags: PropTypes.arrayOf(PropTypes.object),
    t: PropTypes.func,
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  static mapStateToProps = state => ({
    tags: state.tags.data.picture,
  })
  constructor(props) {
    super(props);
    const options = props.tags ? [].concat(props.tags).map(t => ({
      value: t.name,
      text: t.name,
    })) : [];
    this.state = {
      loading: false,
      uploads: [],
      pictureTags: [],
      pictureTypes: [],
      pictureLabels: [],
      degrees: [],
      options,
      fileSizeError: null,
    };
  }
  componentWillMount() {
    const { store } = this.context;
    store.dispatch(fetchTags('picture', true));
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  submit(url, opts = {}) {
    const { store } = this.context;
    return this.setLoading(true)
      .then(() => store.dispatch(fetchM(url, opts)))
      .then(res => res.json())
      .then(result => this.setLoading(false)
        .then(() => this.resetFiles())
        .then(() => result))
      .catch(err => this.setLoading(false)
        .then(() => this.resetFiles())
        .then(() => Promise.reject(err)));
  }
  resetFiles() {
    // temporary fix for upload modal not closing
    this.setState({
      uploads: [],
      pictureTags: [],
      pictureTypes: [],
      pictureLabels: [],
      degrees: [],
    });
  }
  handleSubmit = (event) => {
    event.preventDefault();
    if (this.state.uploads.length > 0) {
      const formData = new FormData(this.form);
      this.state.uploads.forEach((upload, idx) => {
        formData.append(`files_${idx}`, upload.file);
        formData.append(`tags_${idx}`, this.state.pictureTags[idx]);
        formData.append(`labels_${idx}`, this.state.pictureLabels[idx]);
        formData.append(`types_${idx}`, this.state.pictureTypes[idx] || PICTURE_TYPES[0].value);
        formData.append(`degrees_${idx}`, this.state.degrees[idx] || 0);
      });
      if (this.props.onSubmit) {
        this.props.onSubmit(event, formData);
      }
    }
  }
  handleCheckChanges() {
    if (!this.state.hasChanges) {
      this.setState({ hasChanges: true }, () => {
        if (this.props.onChange) {
          this.props.onChange(true);
        }
      });
    }
  }
  handleOnChange = () => {
    const files = Array.prototype.slice.call(this.uploadFiles.files);
    this.uploadFiles.value = null;
    this.setState({ fileSizeError: null });
    if (window.FileReader) {
      const maxSizeInBytes = 10 * 1024 * 1024; // 10MB in bytes
      const validFiles = [];
      const invalidFiles = [];
      files.forEach((file) => {
        if (file.size > maxSizeInBytes) {
          invalidFiles.push(file.name);
        } else {
          validFiles.push(file);
        }
      });

      if (invalidFiles.length > 0) {
        this.setState({
          fileSizeError: `The following files exceed the 10MB size limit and will not be uploaded: ${invalidFiles.join(', ')}. Please reduce the file size and try again.`,
        });
      }
      validFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const { uploads, pictureTags, pictureLabels } = this.state;
          const name = file.name.substring(0, file.name.length - 4);
          uploads.push({
            file,
            name,
            src: reader.result,
          });
          pictureTags.push([]);
          const label = titleCase(symbolToSpace(name));
          pictureLabels.push(label);
          this.setState({
            uploads,
            pictureTags,
            pictureLabels,
          }, () => {
            this.handleCheckChanges();
          });
        };
        reader.readAsDataURL(file);
      });
    }
  }
  handleRemove = (event, idx) => {
    event.preventDefault();
    const { uploads, degrees, pictureTags, pictureTypes, pictureLabels } = this.state;
    uploads.splice(idx, 1);
    degrees.splice(idx, 1);
    pictureTags.splice(idx, 1);
    pictureLabels.splice(idx, 1);
    pictureTypes.splice(idx, 1);
    this.setState({ uploads, degrees, pictureTags, pictureTypes, pictureLabels });
  }
  handleAddFile = (event) => {
    event.preventDefault();
    this.uploadFiles.click();
  }
  handleTagsChange = (e, value, idx) => {
    const { pictureTags } = this.state;
    pictureTags[idx] = value;
    this.setState({ pictureTags });
  }
  handleTypeChange = (e, value, idx) => {
    const { pictureTypes } = this.state;
    pictureTypes[idx] = value;
    this.setState({ pictureTypes });
  }
  handleLabelChange = (e, value, idx) => {
    const { pictureLabels } = this.state;
    const newValue = value.replace(/[\\~#%&*{}/:<>?|"]/, '');
    pictureLabels[idx] = newValue;
    this.setState({ pictureLabels });
  }
  handleAddition = (e, { value }) => {
    const { options } = this.state;
    if (!options.some(o => o.value === value)) {
      this.setState({
        options: [{ text: value, value }, ...options],
      });
    }
  }
  handleRotate = (direction, idx) => {
    const { degrees } = this.state;
    if (!degrees[idx]) {
      degrees[idx] = 0;
    }
    const increment = direction === 'right' ? 1 : -1;
    degrees[idx] += 90 * increment;
    if (degrees[idx] >= 360) {
      degrees[idx] = 0;
    }
    if (degrees[idx] < 0) {
      degrees[idx] = 270;
    }
    this.setState({ degrees });
  }
  render() {
    const { t } = this.props;
    return (
      <form
        encType="multipart/form-data"
        onSubmit={this.handleSubmit}
        name="uploadPictures"
        ref={(ref) => { this.form = ref; }}
      >
        <input
          ref={(ref) => { this.uploadFiles = ref; }}
          type="file"
          onChange={this.handleOnChange}
          accept="image/gif, image/jpeg, image/png"
          multiple
          style={{ display: 'none' }}
        />
        <Grid verticalAlign="middle" stackable>
          <Grid.Row columns="equal">
            <Grid.Column only="computer" />
            <Grid.Column>
              <Grid verticalAlign="middle" columns="equal">
                <Grid.Column>
                  <Button
                    fluid
                    compact
                    primary
                    icon="add"
                    content={t('Images', { ns: TRANSLATION_FILES.translation })}
                    onClick={this.handleAddFile}
                    disabled={this.state.loading}
                  />
                </Grid.Column>
                <Grid.Column>
                  <Button
                    fluid
                    compact
                    type="submit"
                    positive
                    content={t('Upload', { ns: TRANSLATION_FILES.translation })} icon="cloud upload"
                    loading={this.state.loading}
                    disabled={this.state.uploads.length < 1 || this.state.loading}
                  />
                </Grid.Column>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {this.state.fileSizeError && (
          <Message negative>
            <Message.Header>{t('File Size Error', { ns: TRANSLATION_FILES.translation })}</Message.Header>
            <p>{this.state.fileSizeError}</p>
          </Message>
        )}
        <Grid stackable doubling columns={2}>
          <Dimmer style={{ zIndex: 20 }} active={this.state.loading} inverted>
            <Loader indeterminate inline="centered">{t('Uploading', { ns: TRANSLATION_FILES.translation })}</Loader>
          </Dimmer>
          {
            this.state.uploads.length > 0 ?
              this.state.uploads.map((upload, idx) => (
                <Grid.Column key={idx}>
                  <Segment.Group>
                    <Segment secondary>
                      <EditImage
                        src={upload.src}
                        degree={this.state.degrees[idx] || 0}
                      />
                      <Label
                        as="a"
                        corner
                        color="red"
                        icon="delete"
                        disabled={this.state.loading}
                        onClick={e => this.handleRemove(e, idx)}
                      />
                    </Segment>
                    <Segment padded>
                      <Grid columns={1}>
                        <Grid.Column>
                          <Label
                            as="a"
                            attached="top left"
                            icon={{ name: 'repeat', flipped: 'horizontally' }}
                            disabled={this.state.loading}
                            onClick={() => this.handleRotate('left', idx)}
                          />
                          <Label
                            as="a"
                            attached="top right"
                            icon="repeat"
                            disabled={this.state.loading}
                            onClick={() => this.handleRotate('right', idx)}
                          />
                        </Grid.Column>
                        <Grid.Column>
                          <Input
                            fluid
                            placeholder="Label"
                            type="text"
                            value={this.state.pictureLabels[idx]}
                            disabled={this.state.loading}
                            onChange={(e, { value }) => this.handleLabelChange(e, value, idx)}
                          />
                          <Dropdown
                            options={PICTURE_TYPES.map(status => ({
                              ...status,
                              text: t(status.text, { ns: TRANSLATION_FILES.screens }),
                            }))}
                            placeholder={t('Type', { ns: TRANSLATION_FILES.screens })}
                            search
                            selection
                            fluid
                            value={this.state.pictureTypes[idx] || PICTURE_TYPES[0].value}
                            disabled={this.state.loading}
                            onChange={(e, { value }) => this.handleTypeChange(e, value, idx)}
                          />
                          <Dropdown
                            options={this.state.options}
                            placeholder={t('Add tags', { ns: TRANSLATION_FILES.translation })}
                            search
                            selection
                            fluid
                            multiple
                            allowAdditions
                            noResultsMessage={t('Add tags', { ns: TRANSLATION_FILES.translation })}
                            value={this.state.pictureTags[idx]}
                            onAddItem={this.handleAddition}
                            disabled={this.state.loading}
                            onChange={(e, { value }) => this.handleTagsChange(e, value, idx)}
                          />
                        </Grid.Column>
                      </Grid>
                    </Segment>
                  </Segment.Group>
                </Grid.Column>
              ))
            : null
          }
        </Grid>
      </form>
    );
  }
}

export default withTranslation(namespaces, { withRef: true })(connect(
  UploadPictureForm.mapStateToProps, null, null, { withRef: true },
)(UploadPictureForm));



// WEBPACK FOOTER //
// ./src/containers/Pictures/Form.jsx